import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    Grid
} from "@mui/material";
import {
    AccessTime as AccessTimeIcon,
    ArrowDownward as ArrowDownwardIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    EventBusy as EventBusyIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    addDoc,
    serverTimestamp,
    Timestamp,
    limit
} from "firebase/firestore";

function AdminClockActions({ user, orgId }) {
    const [employees, setEmployees] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", severity: "info" });

    // State for filtering
    const [filters, setFilters] = useState({
        employee: "",
        department: ""
    });

    // State for the clock out dialog
    const [clockOutDialog, setClockOutDialog] = useState({
        open: false,
        employee: null,
        reason: "",
        sessionId: null
    });

    useEffect(() => {
        fetchEmployees();
        fetchActiveSessions();
    }, []);

    const fetchEmployees = async () => {
        try {
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const employeeList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setEmployees(employeeList);
        } catch (error) {
            console.error("Error fetching employees:", error);
            setMessage({
                text: "Failed to load employees: " + error.message,
                severity: "error"
            });
        }
    };

    const fetchActiveSessions = async () => {
        setLoading(true);
        try {
            // Clear previous sessions
            setActiveSessions([]);

            // Get all clock-in entries
            const logsRef = collection(db, "organizations", orgId, "timeLogs");
            let q = query(
                logsRef,
                where("type", "==", "clockIn"),
                orderBy("timestamp", "desc")
            );

            // Apply employee filter if set
            if (filters.employee) {
                q = query(q, where("email", "==", filters.employee));
            }

            const snapshot = await getDocs(q);
            const clockIns = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // For each clock-in, check if there's a matching clock-out
            const active = [];

            for (const clockIn of clockIns) {
                // Skip entries without timestamp or userId
                if (!clockIn.timestamp || !clockIn.userId) continue;

                // Get clock-out entries for this user that happened after this clock-in
                const clockOutQuery = query(
                    logsRef,
                    where("userId", "==", clockIn.userId),
                    where("type", "==", "clockOut"),
                    where("timestamp", ">=", clockIn.timestamp)
                );

                const clockOutSnapshot = await getDocs(clockOutQuery);

                // If there are no matching clock-outs, this is an active session
                if (clockOutSnapshot.empty) {
                    // Find the employee details
                    const employee = employees.find(emp => emp.id === clockIn.userId || emp.email === clockIn.email);

                    // Skip if filtered by department and employee doesn't match
                    if (filters.department && employee?.department !== filters.department) {
                        continue;
                    }

                    active.push({
                        id: clockIn.id,
                        email: clockIn.email,
                        userId: clockIn.userId,
                        employeeName: employee?.name || "Unknown",
                        department: employee?.department || "N/A",
                        clockInTime: clockIn.timestamp.toDate(),
                        duration: calculateDuration(clockIn.timestamp.toDate(), new Date())
                    });

                    // Add at most 100 active sessions to prevent performance issues
                    if (active.length >= 100) break;
                }
            }

            setActiveSessions(active);

            if (active.length === 0) {
                setMessage({
                    text: "No active clock-in sessions found.",
                    severity: "info"
                });
            } else {
                setMessage({ text: "", severity: "info" });
            }
        } catch (error) {
            console.error("Error fetching active sessions:", error);
            setMessage({
                text: "Failed to load active sessions: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const calculateDuration = (start, end) => {
        const diffMs = end - start;
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        return `${diffHrs}h ${diffMins}m`;
    };

    const handleFilterChange = (field, value) => {
        setFilters({
            ...filters,
            [field]: value
        });
    };

    const clearFilters = () => {
        setFilters({
            employee: "",
            department: ""
        });
    };

    const applyFilters = () => {
        fetchActiveSessions();
    };

    const openClockOutDialog = (session) => {
        const employee = employees.find(emp => emp.id === session.userId || emp.email === session.email);

        setClockOutDialog({
            open: true,
            employee: employee || { email: session.email },
            sessionId: session.id,
            reason: ""
        });
    };

    const closeClockOutDialog = () => {
        setClockOutDialog({
            ...clockOutDialog,
            open: false
        });
    };

    const handleAdminClockOut = async () => {
        if (!clockOutDialog.reason.trim()) {
            setMessage({
                text: "Please provide a reason for the manual clock out",
                severity: "error"
            });
            return;
        }

        setLoading(true);
        try {
            const { employee, reason, sessionId } = clockOutDialog;

            // Add clock out record
            await addDoc(collection(db, "organizations", orgId, "timeLogs"), {
                userId: employee.id || "unknown",
                email: employee.email,
                type: "clockOut",
                timestamp: serverTimestamp(),
                location: null, // No location for admin clock-out
                adminAction: {
                    adminId: user.uid,
                    adminEmail: user.email,
                    reason,
                    originalClockInId: sessionId,
                    timestamp: Timestamp.fromDate(new Date())
                }
            });

            // Also add an audit log for this action
            await addDoc(collection(db, "organizations", orgId, "adminActionLogs"), {
                action: "manualClockOut",
                employeeId: employee.id || "unknown",
                employeeEmail: employee.email,
                adminId: user.uid,
                adminEmail: user.email,
                reason,
                originalClockInId: sessionId,
                timestamp: serverTimestamp()
            });

            setMessage({
                text: `Successfully clocked out ${employee.name || employee.email}`,
                severity: "success"
            });

            closeClockOutDialog();

            // Refresh the active sessions list
            setTimeout(() => {
                fetchActiveSessions();
            }, 1000);
        } catch (error) {
            console.error("Error with admin clock out:", error);
            setMessage({
                text: "Failed to clock out employee: " + error.message,
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    // Get unique departments for filtering
    const departments = [...new Set(employees.map(emp => emp.department).filter(Boolean))];

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Admin Clock Actions
            </Typography>

            {message.text && (
                <Alert
                    severity={message.severity}
                    sx={{ mb: 2 }}
                    onClose={() => setMessage({ text: "", severity: "info" })}
                >
                    {message.text}
                </Alert>
            )}

            {/* Filters Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Filter Active Sessions
                </Typography>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Employee</InputLabel>
                            <Select
                                value={filters.employee}
                                onChange={(e) => handleFilterChange("employee", e.target.value)}
                                label="Employee"
                            >
                                <MenuItem value="">All Employees</MenuItem>
                                {employees.map(employee => (
                                    <MenuItem key={employee.id} value={employee.email}>
                                        {employee.name || employee.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={filters.department}
                                onChange={(e) => handleFilterChange("department", e.target.value)}
                                label="Department"
                                disabled={departments.length === 0}
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {departments.map(dept => (
                                    <MenuItem key={dept} value={dept}>
                                        {dept}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={applyFilters}
                        disabled={loading}
                    >
                        Find Active Sessions
                    </Button>
                </Box>
            </Paper>

            {/* Active Sessions Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Clock In Time</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <CircularProgress size={24} sx={{ my: 2 }} />
                                </TableCell>
                            </TableRow>
                        ) : activeSessions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No active clock-in sessions found. All employees are currently clocked out.
                                </TableCell>
                            </TableRow>
                        ) : (
                            activeSessions.map(session => (
                                <TableRow key={session.id}>
                                    <TableCell>
                                        {session.employeeName}
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            {session.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{session.department}</TableCell>
                                    <TableCell>{session.clockInTime.toLocaleString()}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={session.duration}
                                            color={
                                                session.duration.startsWith('0h') ? 'success' :
                                                    session.duration.startsWith('1h') ? 'info' :
                                                        session.duration.startsWith('2h') ? 'warning' : 'error'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Clock Out Employee">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<ArrowDownwardIcon />}
                                                onClick={() => openClockOutDialog(session)}
                                            >
                                                Clock Out
                                            </Button>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Clock Out Dialog */}
            <Dialog open={clockOutDialog.open} onClose={closeClockOutDialog}>
                <DialogTitle>
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Admin Clock Out
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to manually clock out {clockOutDialog.employee?.name || clockOutDialog.employee?.email}.
                        Please provide a reason for this action.
                    </DialogContentText>

                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Typography variant="subtitle2">Employee Details:</Typography>
                        <Typography variant="body2">
                            <strong>Name:</strong> {clockOutDialog.employee?.name || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Email:</strong> {clockOutDialog.employee?.email}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Department:</strong> {clockOutDialog.employee?.department || "N/A"}
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        label="Reason for Manual Clock Out"
                        multiline
                        rows={3}
                        value={clockOutDialog.reason}
                        onChange={(e) => setClockOutDialog({ ...clockOutDialog, reason: e.target.value })}
                        required
                        sx={{ mt: 2 }}
                        placeholder="Example: Employee forgot to clock out before leaving"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeClockOutDialog}>Cancel</Button>
                    <Button
                        onClick={handleAdminClockOut}
                        variant="contained"
                        color="error"
                        disabled={loading || !clockOutDialog.reason.trim()}
                        startIcon={loading ? <CircularProgress size={20} /> : <EventBusyIcon />}
                    >
                        Clock Out Employee
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminClockActions;